import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { type ConnectionState, sinceText } from "./connection.js";
import { type IconName, ICONS } from "./icons.js";

/** React port of design/v2 (approved, DL-146). Class names map 1:1 to styles.css; keep them in sync. */

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

export function Icon({ name, small }: { name: IconName; small?: boolean }) {
  return (
    <svg className={cx("ico", small && "ico-sm")} viewBox="0 0 24 24" aria-hidden="true">
      <g dangerouslySetInnerHTML={{ __html: ICONS[name] }} />
    </svg>
  );
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "ghost" | "urgent";
  size?: "md" | "lg";
  block?: boolean;
  icon?: IconName;
};

export function Button({
  variant = "default",
  size = "md",
  block,
  icon,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        "btn",
        variant !== "default" && `btn-${variant}`,
        size === "lg" && "btn-lg",
        block && "btn-block",
        className,
      )}
      {...rest}
    >
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  icon: IconName;
  active?: boolean;
  solid?: boolean;
  dot?: boolean;
};

export function IconButton({
  label,
  icon,
  active,
  solid,
  dot,
  className,
  type = "button",
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx("icon-btn", active && "is-active", solid && "is-solid", className)}
      {...rest}
    >
      <Icon name={icon} />
      {dot && <span className="dot" />}
    </button>
  );
}

export function Chip({
  tone,
  plain,
  children,
}: {
  tone?: "blue" | "coral" | "ok" | "warn" | "dark" | undefined;
  plain?: boolean | undefined;
  children: ReactNode;
}) {
  return <span className={cx("chip", tone, plain && "plain")}>{children}</span>;
}

export function Panel({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx("panel", className)}>
      {(title ?? actions) && (
        <div className="panel-head">
          {title && <h2 className="t-title">{title}</h2>}
          {actions && <div className="actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Card({
  active,
  children,
  className,
}: {
  active?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return <div className={cx("card", active && "is-active", className)}>{children}</div>;
}

export function Seg<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={o.value === value}
          onClick={() => {
            onChange(o.value);
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Field({
  id,
  label,
  hint,
  error,
  children,
  ...input
}: InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
  children?: ReactNode;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children ?? <input id={id} className="input" aria-invalid={error ? true : undefined} {...input} />}
      {error ? (
        <span className="hint" style={{ color: "var(--coral-ink)" }} role="alert">
          {error}
        </span>
      ) : (
        hint && <span className="hint">{hint}</span>
      )}
    </div>
  );
}

export function Switch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <span>{label}</span>
    </label>
  );
}

export function Alert({
  level,
  icon,
  title,
  children,
  actions,
}: {
  level: "normal" | "important" | "urgent";
  icon: IconName;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={cx("alert", level)} role={level === "urgent" ? "alert" : undefined}>
      <Icon name={icon} />
      <div>
        <div className="b">{title}</div>
        {children && <p>{children}</p>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

export function Initials({ text, tone }: { text: string; tone?: "b" | "c" | "g" | "y" }) {
  return <span className={cx("ini", tone)}>{text}</span>;
}

const CONN_TEXT: Record<ConnectionState, string> = {
  live: "اتصال زنده",
  slow: "اتصال کند",
  down: "اتصال قطع",
};

/** Every screen shows this (AGENTS.md §7). When down, the full-width banner tells staff what to do instead. */
export function ConnectionBar({
  state,
  lastOkAt,
  now,
}: {
  state: ConnectionState;
  lastOkAt: number | null;
  now: number;
}) {
  return (
    <span className={cx("conn", state !== "live" && state)} role="status" aria-live="polite">
      <i />
      {CONN_TEXT[state]}، {sinceText(lastOkAt, now)}
    </span>
  );
}

export function ConnectionDownBanner({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="conn-banner" role="alert">
      <Icon name="wifioff" />
      اتصال قطع است. سفارش تلفنی را در دفتر آشپزخانه ثبت کنید.
      <Button onClick={onRetry}>دوباره امتحان کن</Button>
    </div>
  );
}

/** Big round keys (design/v2 Login.html). Digits only, no typing on touch screens. */
export function PinPad({
  length,
  value,
  onChange,
  disabled,
}: {
  length: 4 | 6;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const press = (d: string) => {
    if (!disabled && value.length < length) onChange(value + d);
  };
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
  return (
    <div className="stack" style={{ alignItems: "center" }}>
      <div className="pin-dots" aria-label={`${String(value.length)} رقم از ${String(length)} رقم`}>
        {Array.from({ length }, (_, i) => (
          <i key={i} className={i < value.length ? "on" : undefined} />
        ))}
      </div>
      <div className="pin-pad num" role="group" aria-label="صفحه‌کلید PIN">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            className="pin-key"
            disabled={disabled}
            onClick={() => {
              press(k);
            }}
          >
            {String.fromCharCode(0x06f0 + Number(k))}
          </button>
        ))}
        <span />
        <button
          type="button"
          className="pin-key"
          disabled={disabled}
          onClick={() => {
            press("0");
          }}
        >
          ۰
        </button>
        <button
          type="button"
          className="pin-key ghost"
          aria-label="پاک کردن"
          disabled={disabled}
          onClick={() => {
            onChange(value.slice(0, -1));
          }}
        >
          <Icon name="backspace" />
        </button>
      </div>
    </div>
  );
}

export interface NavItem {
  label: string;
  href: string;
  active?: boolean;
  count?: number;
}
export interface RailItem {
  label: string;
  icon: IconName;
  href: string;
  active?: boolean;
}

/** Manager shell: right-side icon rail, page title, pill navigation, tools (design/v2 Dashboard.html). */
export function AppShell({
  title,
  subtitle,
  nav,
  rail,
  tools,
  onNavigate,
  children,
}: {
  title: string;
  subtitle?: string;
  nav: NavItem[];
  rail: RailItem[];
  tools: ReactNode;
  onNavigate: (href: string) => void;
  children: ReactNode;
}) {
  const go = (href: string) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    onNavigate(href);
  };
  return (
    <div className="app">
      <nav className="rail" aria-label="میان‌برها">
        <div className="brand" aria-hidden="true">
          <Icon name="chef" />
        </div>
        {rail.map((r) => (
          <a
            key={r.href}
            href={r.href}
            onClick={go(r.href)}
            className={cx("icon-btn", r.active && "is-active")}
            aria-label={r.label}
            title={r.label}
            aria-current={r.active ? "page" : undefined}
          >
            <Icon name={r.icon} />
          </a>
        ))}
        <div className="spacer" />
      </nav>
      <header className="top">
        <div className="title">
          <h1 className="t-page">{title}</h1>
          {subtitle && <span className="muted">{subtitle}</span>}
        </div>
        <nav className="pills" aria-label="بخش‌ها">
          {nav.map((n) => (
            <a key={n.href} href={n.href} onClick={go(n.href)} aria-current={n.active ? "page" : undefined}>
              {n.label}
              {n.count ? (
                <span className="count num">
                  {String(n.count).replace(/\d/g, (d) => String.fromCharCode(0x06f0 + Number(d)))}
                </span>
              ) : null}
            </a>
          ))}
        </nav>
        <div className="tools">{tools}</div>
      </header>
      <main className="main">{children}</main>
    </div>
  );
}
